import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Trash2, X } from 'lucide-react';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'default';
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      confirmRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  const iconMap = {
    danger: <Trash2 size={20} />,
    warning: <AlertTriangle size={20} />,
    default: <AlertTriangle size={20} />,
  };

  const iconBgMap = {
    danger: 'bg-[var(--danger-muted)] text-[var(--danger)]',
    warning: 'bg-[var(--warning-muted)] text-[var(--warning)]',
    default: 'bg-[var(--info-muted)] text-[var(--info)]',
  };

  const confirmBtnMap = {
    danger: 'bg-[var(--danger)] hover:opacity-90 text-white',
    warning: 'bg-[var(--warning)] hover:opacity-90 text-white',
    default: 'btn-primary-solid',
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="confirm-backdrop"
            className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-[8px]"
            onClick={onCancel}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          />
          <motion.div
            key="confirm-dialog"
            className="fixed z-[201] left-1/2 top-1/2 w-[min(420px,92vw)]"
            style={{ x: '-50%', y: '-50%' }}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-message"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            <div
              className="rounded-2xl border shadow-xl overflow-hidden"
              style={{
                background: 'var(--bg-elevated)',
                backdropFilter: 'blur(var(--glass-blur))',
                WebkitBackdropFilter: 'blur(var(--glass-blur))',
                borderColor: 'var(--glass-border)',
                boxShadow: 'var(--glass-shadow)',
              }}
            >
              <div className="flex items-start gap-3 p-5">
                <div
                  className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl ${iconBgMap[variant]}`}
                >
                  {iconMap[variant]}
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    id="confirm-dialog-title"
                    className="text-base font-semibold"
                    style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}
                  >
                    {title}
                  </h3>
                  <p
                    id="confirm-dialog-message"
                    className="mt-1 text-sm leading-relaxed"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {message}
                  </p>
                </div>
                <button
                  onClick={onCancel}
                  className="flex-shrink-0 p-1 rounded-lg transition-colors hover:bg-black/5"
                  style={{ color: 'var(--text-muted)' }}
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>

              <div
                className="flex justify-end gap-2.5 px-5 py-3.5"
                style={{ borderTop: '1px solid var(--glass-border)' }}
              >
                <button
                  onClick={onCancel}
                  className="btn-secondary px-4 py-2 text-sm"
                >
                  {cancelLabel}
                </button>
                <button
                  ref={confirmRef}
                  onClick={onConfirm}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl transition-colors cursor-pointer ${confirmBtnMap[variant]}`}
                  style={{ fontFamily: 'var(--font-ui)' }}
                >
                  {confirmLabel}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
